import { fmtSize } from '@shared/lib/numbers';
import { PRICE_KEY_MULTIPLIER, getPriceKey, setLevel } from './model';
import { renderLevels as renderLevelsView } from './view';
import type { Book, OrderbookEvent, OrderbookStore } from '../types';

interface DOMElements { booksRow: HTMLElement | null; }

interface CreateControllerParams {
  el: DOMElements;
  store: OrderbookStore;
  onRenderBookAfterLevels?: (assetId: string, bestBid?: number, bestAsk?: number) => void;
}

export function createOrderbookController({ el, store, onRenderBookAfterLevels }: CreateControllerParams) {
  const books = store.books;
  let pendingDrainScheduled = false;
  let backgroundRenderTimer: ReturnType<typeof setInterval> | undefined;
  const obLog = (...args: unknown[]): void => { try { console.log('[OB]', ...args); } catch {} };

  function ensurePanel(assetId: string, name: string): Book {
    if (books.has(assetId)) return books.get(assetId)!;
    const panel = document.createElement('div');
    panel.className = 'panel';
    (panel as any).dataset.assetId = assetId;
    const title = document.createElement('div');
    title.className = 'title';
    title.textContent = `${name} (${assetId.slice(0, 10)}…)`;
    panel.appendChild(title);
    const tob = document.createElement('div');
    tob.className = 'tob';
    const bestBid = document.createElement('div');
    const bestAsk = document.createElement('div');
    try { bestBid.style.display = 'none'; } catch {}
    try { bestAsk.style.display = 'none'; } catch {}
    const spread = document.createElement('div');
    const spreadText = document.createElement('span');
    const midText = document.createElement('span');
    spread.appendChild(spreadText);
    spread.appendChild(midText);
    tob.appendChild(bestBid);
    tob.appendChild(bestAsk);
    tob.appendChild(spread);
    panel.appendChild(tob);
    const grid = document.createElement('div');
    grid.className = 'book-grid';
    const asksBox = document.createElement('div');
    const asksTitle = document.createElement('div');
    asksTitle.className = 'side-title ask';
    asksTitle.textContent = 'Asks (price / size)';
    const asksLevels = document.createElement('div');
    asksLevels.className = 'levels';
    (asksLevels as any).dataset.assetId = assetId;
    (asksLevels as any).dataset.side = 'ASK';
    asksBox.appendChild(asksTitle);
    asksBox.appendChild(asksLevels);
    const bidsBox = document.createElement('div');
    const bidsTitle = document.createElement('div');
    bidsTitle.className = 'side-title bid';
    bidsTitle.textContent = 'Bids (price / size)';
    const bidsLevels = document.createElement('div');
    bidsLevels.className = 'levels';
    (bidsLevels as any).dataset.assetId = assetId;
    (bidsLevels as any).dataset.side = 'BID';
    bidsBox.appendChild(bidsTitle);
    bidsBox.appendChild(bidsLevels);
    grid.appendChild(asksBox);
    grid.appendChild(bidsBox);
    panel.appendChild(grid);
    try {
      const children = Array.from(el.booksRow?.children || []);
      if (children.length === 0) panel.classList.add('team-a');
      else if (children.length === 1) panel.classList.add('team-b');
    } catch {}
    el.booksRow?.appendChild(panel);
    const book: Book = {
      assetId,
      name,
      get decimals() { try { return (store?.priceDecimalsById?.[assetId]) ?? 2; } catch { return 2; } },
      bids: new Map(),
      asks: new Map(),
      bidPrices: [],
      askPrices: [],
      bestBid,
      bestAsk,
      spread,
      spreadText,
      midText,
      bidsLevels,
      asksLevels,
      dirty: false,
    };
    books.set(assetId, book);
    return book;
  }

  function buildPriceList(map: Map<string, number>, current: number[], isBid: boolean): number[] {
    const seen = new Set<string>();
    const result: number[] = [];
    if (map && typeof map.entries === 'function') {
      for (const [key, size] of map.entries()) {
        const num = Number(key);
        if (!Number.isFinite(num)) continue;
        if (!isFinite(size) || size <= 0) continue;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(num / PRICE_KEY_MULTIPLIER);
      }
    }
    if (Array.isArray(current)) {
      for (const price of current) {
        const key = getPriceKey(price);
        if (key == null || seen.has(key)) continue;
        if (map && typeof map.has === 'function' && !map.has(key)) continue;
        seen.add(key);
        result.push(price);
      }
    }
    result.sort((a, b) => (isBid ? b - a : a - b));
    return result;
  }

  function validateBookDOM(book: Book, _assetId: string): boolean {
    try {
      if (!book.bidsLevels || !book.asksLevels) return false;
      if (!(book.bidsLevels as any).isConnected || !(book.asksLevels as any).isConnected) return false;
      return true;
    } catch { return false; }
  }

  function renderBook(book: Book, _assetId: string): void {
    const assetId = _assetId;
    const dec = store?.priceDecimalsById?.[assetId] ?? 2;
    book.bidPrices = buildPriceList(book.bids, book.bidPrices, true);
    book.askPrices = buildPriceList(book.asks, book.askPrices, false);
    const bb = book.bidPrices[0];
    const ba = book.askPrices[0];
    const bbSize = bb != null ? book.bids.get(getPriceKey(bb)!) : 0;
    const baSize = ba != null ? book.asks.get(getPriceKey(ba)!) : 0;
    book.bestBid.textContent = bb != null ? `Best Bid: ${bb.toFixed(dec)} (${fmtSize(bbSize!)})` : 'Best Bid: -';
    book.bestAsk.textContent = ba != null ? `Best Ask: ${ba.toFixed(dec)} (${fmtSize(baSize!)})` : 'Best Ask: -';
    if (bb != null && ba != null) {
      const spreadRaw = Number(ba) - Number(bb);
      const spreadRounded = Number(spreadRaw.toFixed(2));
      const mid = (Number(ba) + Number(bb)) / 2;
      book.spreadText.textContent = `Spread: ${spreadRounded.toFixed(2)}`;
      book.midText.textContent = `  Mid: ${mid.toFixed(2)}`;
      book.spreadText.className = spreadRounded <= 0.03 ? 'bid' : (spreadRounded <= 0.09 ? 'warn' : 'ask');
      book.midText.className = '';
    } else {
      book.spreadText.textContent = 'Spread: -';
      book.midText.textContent = '';
      book.spreadText.className = 'muted';
      book.midText.className = '';
    }
    const N = 20;
    const askArrRaw = book.askPrices.slice(0, N).map((p) => { const key = getPriceKey(p); return { price: p, size: key != null ? book.asks.get(key) ?? null : null }; });
    askArrRaw.reverse();
    const bidArrRaw = book.bidPrices.slice(0, N).map((p) => { const key = getPriceKey(p); return { price: p, size: key != null ? book.bids.get(key) ?? null : null }; });
    const askArr: Array<{ price: number | null; size: number | null }> = askArrRaw.slice();
    while (askArr.length < N) askArr.unshift({ price: null, size: null });
    const bidArr: Array<{ price: number | null; size: number | null }> = bidArrRaw.slice();
    while (bidArr.length < N) bidArr.push({ price: null, size: null });
    const maxAcross = Math.max(
      askArr.reduce((m, e) => { const v = Number(e?.size); return isFinite(v) && v > m ? v : m; }, 0),
      bidArr.reduce((m, e) => { const v = Number(e?.size); return isFinite(v) && v > m ? v : m; }, 0)
    );
    renderLevelsView(book.asksLevels, askArr as any, false, maxAcross);
    renderLevelsView(book.bidsLevels, bidArr as any, true, maxAcross);
    if (typeof onRenderBookAfterLevels === 'function') onRenderBookAfterLevels(assetId, bb, ba);
  }

  function handleRenderError(err: Error): void {
    store.renderScheduled = false;
    store.renderFailures++;
    store.obLastRenderError = err?.message ?? String(err);
    store.obState = 'render_error';
  }

  function renderAll(): void {
    store.renderAttempts++;
    if (store.activeRender) { store.renderScheduled = false; return; }
    store.activeRender = true;
    try {
      let anyRendered = false; let allSucceeded = true;
      books.forEach((book, assetId) => {
        if (!book.dirty) return;
        if (!validateBookDOM(book, assetId)) { book.dirty = false; allSucceeded = false; return; }
        try { renderBook(book, assetId); book.dirty = false; anyRendered = true; }
        catch (err) { book.dirty = false; allSucceeded = false; }
      });
      if (anyRendered && allSucceeded) {
        store.obLastRenderTs = Date.now();
        store.renderSuccesses++;
        store.obLastRenderError = null;
        // Do not mark degraded based solely on a slow frame; rely on lag/backlog heuristics
        if (store.obState !== 'closed' && store.obState !== 'error') { store.obState = 'live'; }
      } else if (!allSucceeded) {
        store.renderFailures++;
        throw new Error('Some books failed to render');
      }
      store.renderScheduled = false;
      store.activeRender = false;
      if (store.pendingEvents.length > 0) {
        schedulePendingDrain();
      }
    } catch (err) {
      store.activeRender = false; store.renderScheduled = false; handleRenderError(err as Error);
    }
  }

  function scheduleRender(): void {
    if (store.renderScheduled) return;
    store.renderScheduled = true;
    try {
      const useRaf = typeof requestAnimationFrame !== 'undefined' && !(document && (document as any).hidden);
      if (useRaf) {
        requestAnimationFrame(() => { try { renderAll(); } catch (err) { handleRenderError(err as Error); } });
      } else {
        setTimeout(() => { try { renderAll(); } catch (err) { handleRenderError(err as Error); } }, 0);
      }
    } catch (err) { handleRenderError(err as Error); }
  }

  function applyEvents(events: OrderbookEvent[]): void {
    if (store.activeRender) {
      store.pendingEvents.push(...events);
      // Cap pending backlog to avoid unbounded growth
      if (store.pendingEvents.length > 500) { store.pendingEvents = store.pendingEvents.slice(-500); }
      schedulePendingDrain();
      return;
    }
    applyEventList(events);
    scheduleRender();
  }

  function applyText(text: string): boolean {
    let appliedAny = false; let sawError = false;
    try {
      const lines = String(text).split(/\r?\n/);
      let totalParsed = 0; const typeCounts = new Map<string, number>(); const unknownTypes = new Set<string>();
      for (const line of lines) {
        const s = String(line || '').trim();
        if (!s) continue;
        try {
          const parsed = JSON.parse(s);
          let list: OrderbookEvent[] | null = null;
          // Capture container-level fields to propagate into child events when missing
          const parentAssetId: string | undefined =
            (parsed && ((parsed as any).asset_id || (parsed as any).assetId || (parsed as any).asset ||
              ((parsed as any).market && (((parsed as any).market.asset_id) || ((parsed as any).market.assetId))) ||
              (parsed as any).market_id || (parsed as any).marketId || (parsed as any).token_id || (parsed as any).tokenId))
              ? String((parsed as any).asset_id || (parsed as any).assetId || (parsed as any).asset ||
                  ((parsed as any).market && (((parsed as any).market.asset_id) || ((parsed as any).market.assetId))) ||
                  (parsed as any).market_id || (parsed as any).marketId || (parsed as any).token_id || (parsed as any).tokenId)
              : undefined;
          if (Array.isArray(parsed)) list = parsed as any;
          else if ((parsed as any)?.events && Array.isArray((parsed as any).events)) list = (parsed as any).events as OrderbookEvent[];
          else if ((parsed as any)?.event_type) list = [parsed as any];
          if (Array.isArray(list) && list.length) {
            // Propagate parent asset id if child events lack it
            if (parentAssetId) {
              for (const ev of list as any[]) {
                if (ev && !ev.asset_id && !ev.assetId) ev.asset_id = parentAssetId;
              }
            }
            totalParsed += list.length;
            for (const e of list as any[]) { const t = String(e && e.event_type || ''); const k = t.toLowerCase() || 'unknown'; typeCounts.set(k, (typeCounts.get(k) || 0) + 1); if (!k) unknownTypes.add(t); }
            // Detect trade/fill-like events without re-parsing in app.ts
            for (const evtAny of list as any[]) {
              try {
                const t = String((evtAny && evtAny.event_type) || '').toLowerCase();
                if (t === 'trade' || t === 'fill' || t === 'order_filled' || t === 'order_partially_filled') {
                  try { (store as any).obLastTradeLikeTs = Date.now(); } catch {}
                }
              } catch {}
            }
            // Apply book-affecting events to the model
            const isBookAffecting = (e: any): boolean => {
              const t = String(e && e.event_type || '').toLowerCase();
              if (!t) return false;
              if (t === 'book' || t === 'price_change') return true;
              // Common order-level event types that change depth
              if (t.includes('order') || t === 'open' || t === 'cancel' || t === 'reduce' || t === 'update') return true;
              // Some feeds emit 'fill'/'trade' with price/side; treat as depth-reducing
              if (t === 'fill' || t === 'trade' || t.includes('filled')) return true;
              return false;
            };
            const applicable = (list as any[]).filter((e) => isBookAffecting(e)) as OrderbookEvent[];
            if (applicable.length) { applyEvents(applicable); appliedAny = true; }
          }
        } catch (err) {
          sawError = true; try { store.obLastApplyErrorTs = Date.now(); } catch {}
        }
      }
      try {
        if (totalParsed > 0) {
          const summary: Record<string, number> = {}; typeCounts.forEach((v, k) => (summary[k] = v));
          obLog('parsed_events', { total: totalParsed, byType: summary });
        }
      } catch {}
    } catch (err) { sawError = true; try { store.obLastApplyErrorTs = Date.now(); } catch {} }
    return appliedAny && !sawError;
  }

  function schedulePendingDrain(): void {
    if (pendingDrainScheduled) return;
    pendingDrainScheduled = true;
    const run = (): void => {
      pendingDrainScheduled = false;
      if (store.activeRender) { schedulePendingDrain(); return; }
      if (!Array.isArray(store.pendingEvents) || store.pendingEvents.length === 0) return;
      const hidden = (typeof document !== 'undefined') && (document as any).hidden;
      const batchSize = hidden ? 500 : 200;
      const batch = store.pendingEvents.splice(0, batchSize);
      try { applyEventList(batch); } catch {}
      scheduleRender();
      if (store.pendingEvents.length > 0) {
        pendingDrainScheduled = true;
        try {
          if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(run);
          else setTimeout(run, 0);
        } catch { setTimeout(run, 0); }
      }
    };
    try {
      if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(run);
      else setTimeout(run, 0);
    } catch { setTimeout(run, 0); }
  }

  function applyEventList(events: OrderbookEvent[]): void {
    let appliedCount = 0;
    // Coalesce by asset; keep last snapshot and last change per price/side
    interface Coalesced { snapshot?: any; abs: Map<string, { side: string; price: string; size: string }>; delta: Map<string, { side: string; price: string; delta: number }>; }
    const byAsset = new Map<string, Coalesced>();
    const getRec = (assetId: string): Coalesced => { let r = byAsset.get(assetId); if (!r) { r = { abs: new Map(), delta: new Map() }; byAsset.set(assetId, r); } return r; };
    const toNum = (v: any): number => { const n = Number(v); return isFinite(n) ? n : 0; };
    const normSide = (s: any): 'BUY' | 'SELL' | '' => {
      const t = String(s || '').toUpperCase();
      if (t === 'BUY' || t === 'SELL') return t as any;
      if (t === 'BID') return 'BUY' as any;
      if (t === 'ASK') return 'SELL' as any;
      return '' as any;
    };
    let cntSnapshot = 0, cntAbs = 0, cntDelta = 0, cntSkipSide = 0, cntMissingPrice = 0, cntMissingAssetChange = 0;
    const dropSamples: Array<{ reason: string; type: string; side?: string; price?: string; have?: Record<string, boolean> }> = [];
    const rawSides = new Set<string>();
    for (const raw of events as any[]) {
      const evt: any = raw || {};
      const assetIdDirect = evt && (evt.asset_id ?? evt.assetId ?? evt.asset ?? evt.token_id ?? evt.tokenId ?? evt.token ?? evt.market_id ?? evt.marketId);
      const assetIdMarket = evt && evt.market && (evt.market.asset_id || evt.market.assetId || evt.market.id);
      const evtAssetId: string = String(assetIdDirect || assetIdMarket || '') || '';
      const t = String(evt.event_type || '').toLowerCase();
      if (t === 'book') {
        if (!evtAssetId) { continue; }
        const rec = getRec(evtAssetId);
        rec.snapshot = evt;
        rec.abs.clear();
        rec.delta.clear();
        cntSnapshot++;
        continue;
      }
      if (t === 'price_change') {
        const list = Array.isArray((evt as any).price_changes)
          ? (evt as any).price_changes
          : (Array.isArray((evt as any).changes) ? (evt as any).changes : []);
        for (const ch of list) {
          const rawSide = String((ch as any).side || ''); rawSides.add(rawSide);
          const side = normSide((ch as any).side);
          const price = String((ch as any).price);
          const sizeStr = String((ch as any).size);
          // derive asset per change
          const chAssetIdRaw = (ch as any).asset_id ?? (ch as any).asset ?? ((ch as any).market && ((ch as any).market.asset_id || (ch as any).market.assetId)) ?? (ch as any).market_id ?? (ch as any).marketId ?? (ch as any).token_id ?? (ch as any).tokenId ?? (ch as any).token ?? evtAssetId;
          const chAssetId = String(chAssetIdRaw || '') || '';
          if (!chAssetId) { cntMissingAssetChange++; if (dropSamples.length < 5) dropSamples.push({ reason: 'missing_asset_change', type: t }); continue; }
          if (!side) { cntSkipSide++; if (dropSamples.length < 5) dropSamples.push({ reason: 'skip_side', type: t, side: rawSide, price }); continue; }
          if (!price) { cntMissingPrice++; if (dropSamples.length < 5) dropSamples.push({ reason: 'missing_price', type: t, side: rawSide }); continue; }
          const key = `${side}|${price}`;
          const rec = getRec(chAssetId);
          rec.abs.set(key, { side, price, size: sizeStr });
          cntAbs++;
          // Update order index for future cancels by synthetic order key if present
          try {
            const orderId = (ch as any).order_id || (ch as any).orderId || '';
            if (orderId && store.orderIndex) store.orderIndex.set(String(orderId), { assetId: chAssetId, side: side as any, price });
          } catch {}
        }
        continue;
      }
      // Order-level events
      const rawSide = String(evt.side || (evt.order && evt.order.side) || ''); rawSides.add(rawSide);
      let side = normSide(evt.side || (evt.order && evt.order.side));
      let price = String(evt.price ?? (evt.order && evt.order.price) ?? '');
      const evtAsset = evtAssetId;
      if (!evtAsset) { if (dropSamples.length < 5) dropSamples.push({ reason: 'missing_asset', type: t, have: {
        asset_id: !!(evt && (evt.asset_id || evt.assetId)),
        asset: !!(evt && evt.asset),
        market_asset_id: !!(evt && evt.market && (evt.market.asset_id || evt.market.assetId)),
        market_id: !!(evt && (evt.market_id || evt.marketId)),
        token_id: !!(evt && (evt.token_id || evt.tokenId)),
      }}); continue; }
      // Try to resolve from order index before deciding to skip
      try {
        const orderId = evt.order_id || evt.orderId || (evt.order && (evt.order.id || evt.order.order_id || evt.order.orderId));
        if (orderId && store.orderIndex && store.orderIndex.has(String(orderId))) {
          const meta = store.orderIndex.get(String(orderId))!;
          if (!side && meta.side) side = meta.side as any;
          if (!price && meta.price) price = meta.price;
        }
      } catch {}
      if (!side) { cntSkipSide++; if (dropSamples.length < 5) dropSamples.push({ reason: 'skip_side', type: t, side: rawSide, price }); continue; }
      if (!price) { cntMissingPrice++; if (dropSamples.length < 5) dropSamples.push({ reason: 'missing_price', type: t, side: rawSide }); continue; }
      const key = `${side}|${price}`;
      // Update/seed order index from placement-like events
      try {
        const orderId = evt.order_id || evt.orderId || (evt.order && (evt.order.id || evt.order.order_id || evt.order.orderId));
        if (orderId && store.orderIndex) store.orderIndex.set(String(orderId), { assetId: evtAsset, side: side as any, price });
      } catch {}
      // Prefer absolute fields if present
      const absFields = [evt.remaining_size, evt.size_after, evt.new_size, evt.size_remaining];
      const absField = absFields.find((x: any) => x != null && x !== '');
      if (absField != null) {
        const rec = getRec(evtAsset);
        rec.abs.set(key, { side, price, size: String(absField) });
        cntAbs++;
        continue;
      }
      // Else derive delta by event semantics or explicit delta fields
      let delta = 0;
      const explicitDelta = evt.delta ?? evt.size_delta;
      if (explicitDelta != null) delta = toNum(explicitDelta);
      else {
        const sizeNum = toNum(evt.size ?? (evt.order && evt.order.size));
        if (t.includes('cancel') || t.includes('filled') || t === 'fill' || t === 'trade' || t.includes('reduce') || t.includes('update')) delta = -Math.abs(sizeNum);
        else if (t.includes('open') || t.includes('placed') || t.includes('create')) delta = Math.abs(sizeNum);
      }
      if (delta !== 0) {
        const rec = getRec(evtAsset);
        const prev = rec.delta.get(key)?.delta || 0;
        rec.delta.set(key, { side, price, delta: prev + delta });
        cntDelta++;
      }
    }
    // Apply per-asset in order: snapshot -> absolute -> delta
    byAsset.forEach((rec, assetId) => {
      let book = books.get(assetId);
      if (!book && typeof store === 'object') {
        try { const name = store?.outcomesById?.[assetId] || assetId.slice(0, 8); if (typeof ensurePanel === 'function') book = ensurePanel(assetId, name); }
        catch {}
      }
      if (!book) return;
      const markDirty = () => { book!.dirty = true; };
      const considerPrice = (priceStr: string | number | undefined) => {
        try {
          const s = String(priceStr || '').trim();
          const dot = s.indexOf('.');
          if (dot < 0) return;
          const dec = Math.max(0, Math.min(6, s.length - dot - 1));
          const cur = store?.priceDecimalsById?.[assetId] ?? 2;
          if (dec > cur) { store.priceDecimalsById[assetId] = dec; markDirty(); }
        } catch {}
      };
      if (rec.snapshot) {
        try {
          book.bids.clear();
          book.asks.clear();
          book.bidPrices = [];
          book.askPrices = [];
          for (const l of (rec.snapshot.bids || [])) { considerPrice(l?.price); setLevel(book, true, l.price, l.size); }
          for (const l of (rec.snapshot.asks || [])) { considerPrice(l?.price); setLevel(book, false, l.price, l.size); }
          markDirty();
          appliedCount++;
        } catch {}
      }
      if (rec.abs.size) {
        rec.abs.forEach((ch) => {
          const isBid = ch.side === 'BUY';
          considerPrice(ch.price);
          setLevel(book!, isBid, ch.price, ch.size);
        });
        markDirty();
        appliedCount += rec.abs.size;
      }
      if (rec.delta.size) {
        rec.delta.forEach((d) => {
          const isBid = d.side === 'BUY';
          const key = getPriceKey(d.price as any);
          const map = isBid ? book!.bids : book!.asks;
          const cur = key != null ? map.get(key) || 0 : 0;
          const next = Math.max(0, cur + d.delta);
          considerPrice(d.price);
          setLevel(book!, isBid, d.price, String(next));
        });
        markDirty();
        appliedCount += rec.delta.size;
      }
    });
    try {
      obLog('batch_apply', {
        assets: byAsset.size,
        snapshot: cntSnapshot,
        abs: cntAbs,
        delta: cntDelta,
        skipSide: cntSkipSide,
        missingPrice: cntMissingPrice,
        missingAssetChange: cntMissingAssetChange,
        sides: Array.from(rawSides).slice(0, 6),
        pending: store.pendingEvents.length,
      });
      // If events were parsed but we skipped due to missing asset_id, side, or price, log samples
      if (byAsset.size === 0 && events && (events as any[]).length > 0) {
        const first = events[0] as any;
        const have: Record<string, boolean> = {
          asset_id: !!(first && (first.asset_id || first.assetId)),
          asset: !!(first && first.asset),
          market_asset_id: !!(first && first.market && (first.market.asset_id || first.market.assetId)),
          market_id: !!(first && (first.market_id || first.marketId)),
          token_id: !!(first && (first.token_id || first.tokenId)),
        };
        if (dropSamples.length < 5) dropSamples.push({ reason: 'missing_asset', type: String(first && first.event_type || '').toLowerCase(), have });
        try {
          obLog('evt_keys', Array.isArray(first) ? [] : Object.keys(first || {}));
          if (first && first.changes && Array.isArray(first.changes) && first.changes.length > 0) {
            obLog('change_keys', Object.keys(first.changes[0] || {}));
          }
        } catch {}
      }
      if (dropSamples.length > 0) {
        obLog('drop_samples', dropSamples);
      }
      // Only advance liveness if something actually applied
      if (appliedCount > 0) {
        store.obLastMessageTs = Date.now();
        try { store.obLastBookTs = store.obLastMessageTs; } catch {}
      }
    } catch {}
  }

  function anyDirty(): boolean {
    try {
      for (const [_id, b] of books) { if (b && b.dirty) return true; }
    } catch {}
    return false;
  }

  function ensureBackgroundRenderTicker(): void {
    try {
      if (typeof document === 'undefined') return;
      const onVisChange = () => {
        const hidden = (document as any).hidden;
        try { if (backgroundRenderTimer) clearInterval(backgroundRenderTimer); } catch {}
        backgroundRenderTimer = undefined;
        if (hidden) {
          backgroundRenderTimer = setInterval(() => {
            try {
              if (store.pendingEvents.length > 0 || anyDirty()) {
                schedulePendingDrain();
                scheduleRender();
              }
            } catch {}
          }, 16);
        }
      };
      document.removeEventListener('visibilitychange', onVisChange as any);
      document.addEventListener('visibilitychange', onVisChange as any);
      // Initialize once
      onVisChange();
    } catch {}
  }

  // Start background render ticker once controller is created
  ensureBackgroundRenderTicker();

  function reorderBooks(teamAId?: string, teamBId?: string): void {
    try {
      const row = el.booksRow; if (!row) return; const children = Array.from(row.children); if (!children || children.length === 0) return;
      const byId = new Map(children.map((p) => [((p as any).dataset?.assetId as string | undefined), p]));
      const left = byId.get(teamAId); const right = byId.get(teamBId); const rest = children.filter((p) => p !== left && p !== right);
      children.forEach((p) => { try { (p as HTMLElement).classList.remove('team-a'); (p as HTMLElement).classList.remove('team-b'); } catch {} });
      row.innerHTML = '';
      if (left) { (left as HTMLElement).classList.add('team-a'); row.appendChild(left); }
      if (right) { (right as HTMLElement).classList.add('team-b'); row.appendChild(right); }
      for (const r of rest) { try { (r as HTMLElement).classList.remove('team-a'); (r as HTMLElement).classList.remove('team-b'); } catch {}; row.appendChild(r); }
    } catch {}
  }

  return { ensurePanel, applyEvents, applyText, reorderBooks };
}
